import { useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Chip, CircularProgress, IconButton,
  Paper, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useDevicesQuery, useUpdateDeviceNicknameMutation } from './hooks/useDevicesQuery';
import { DeviceNicknameDialog } from './DeviceNicknameDialog';
import type { Device } from '../../services/devicesApiService';

export function DevicesPage() {
  const { data: devices, isLoading, isError } = useDevicesQuery();
  const updateMutation = useUpdateDeviceNicknameMutation();

  const [editTarget, setEditTarget] = useState<Device | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const showMessage = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleUpdateNickname = async (nickname: string) => {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ id: editTarget.id, nickname: nickname || null });
      setEditTarget(null);
      showMessage('Device nickname updated');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        showMessage('A device with this nickname already exists in the lab.', 'error');
      } else {
        showMessage('Failed to update nickname', 'error');
      }
    }
  };

  return (
    <Box p={3}>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={600}>Device Management</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Assign nicknames to make devices easy to identify across the lab.
        </Typography>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">Failed to load devices</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Display Name</TableCell>
                <TableCell>Device UID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No devices registered yet. Devices appear automatically when tests are performed.
                  </TableCell>
                </TableRow>
              )}
              {devices?.map((device) => (
                <TableRow key={device.id}>
                  <TableCell>
                    {device.nickname
                      ? <strong>{device.nickname}</strong>
                      : <Typography component="span" variant="body2" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                          {device.deviceUid}
                        </Typography>
                    }
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                    {device.deviceUid}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={device.status}
                      color={device.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {device.lastSeenAt
                      ? new Date(device.lastSeenAt).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit nickname">
                      <IconButton size="small" onClick={() => setEditTarget(device)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeviceNicknameDialog
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        device={editTarget}
        onSubmit={handleUpdateNickname}
        isLoading={updateMutation.isPending}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
